import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Params, Router, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { AppState } from '@reducers/interfaces';
import { getPeriod, parseDate, toISODateString } from '@shiftbase-com/ts-shared/utilities';
import { isValid } from 'date-fns';
import { Observable, of as observableOf } from 'rxjs';
import { combineLatest, first, map, tap } from 'rxjs/operators';

import { TimesheetFilterState } from '../../../../reducers/page-filters/page-filters.model';
import { getTimesheetFilters, PageFiltersService } from '../../../../reducers/page-filters/page-filters.service';
import { lazySelect } from '../../../../shared/lazy-select.observable';
import { TimesheetHelperService } from '../timesheet-helper.service';

@Injectable()
export class TimesheetResolver {
  private router = inject(Router);
  private store = inject<Store<AppState>>(Store);
  private pageFiltersService = inject(PageFiltersService);
  private timesheetHelperService = inject(TimesheetHelperService);

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> {
    return this.activeDate(route).pipe(
      tap((date) => {
        const periodType = route.data['periodType'];
        const period = getPeriod(periodType, date);

        this.pageFiltersService.setFilter('timesheet', 'period', {
          minDate: toISODateString(period.start),
          maxDate: toISODateString(period.end),
        });
      }),
      first(),
    );
  }

  private activeDate(route: ActivatedRouteSnapshot): Observable<Date> {
    return observableOf(route.params).pipe(
      map((params: Params) => params['date']),
      combineLatest(
        this.store.pipe(
          lazySelect(getTimesheetFilters),
          map((filter: TimesheetFilterState) =>
            filter && filter.period ? filter.period.minDate : toISODateString(new Date()),
          ),
        ),
      ),
      map(([routeDate, selectedDate]) => (!routeDate ? selectedDate : routeDate)),
      map((selectedDate) => {
        const parsed = parseDate(selectedDate);

        return isValid(parsed) ? parsed : new Date();
      }),
    );
  }
}
